/**
 * ComposicionFlota — tabla compacta tipo × uso con precarga (DEC-019) + override por fila (DEC-025).
 *
 * Modelo de datos de una fila:
 *   {
 *     id, tipo_id, uso_id, num_vehiculos, km_anio,
 *     consumo, sin_pct, valor_unitario,        // valores efectivos (los que usa el cálculo)
 *     campos_manuales: [],                     // DEC-024 · campos que el comercial tocó (no se recalculan)
 *     overrides: {                             // DEC-025 · overrides opcionales de los maestros
 *       consumo_base?, sin_pct_base?, valor_unitario_base?,
 *       multiplicador_consumo?, multiplicador_siniestros?
 *     }
 *   }
 *
 * Sincroniza totales hacia el padre: onChange(filas, totales).
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Plus, X, ChevronRight, ChevronDown, RotateCcw } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const authAxios = axios.create();
authAxios.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("mobilsafe_token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

function uid() { return Math.random().toString(36).slice(2, 10); }
const round1 = (n) => Math.round((Number(n) || 0) * 10) / 10;

export function calcularTotales(filas = []) {
  const num = filas.reduce((s, f) => s + (Number(f.num_vehiculos) || 0), 0);
  const kmTotal = filas.reduce((s, f) => s + (Number(f.num_vehiculos) || 0) * (Number(f.km_anio) || 0), 0);
  const valorTotal = filas.reduce((s, f) => s + (Number(f.num_vehiculos) || 0) * (Number(f.valor_unitario) || 0), 0);
  const km_medio = num > 0 ? Math.round(kmTotal / num) : 0;
  return { num_vehiculos_total: num, km_anio_medio: km_medio, km_total: kmTotal, valor_activo_total: valorTotal };
}

// Calcula los valores efectivos a partir del tipo, uso, campos manuales y overrides.
// Respeta campos_manuales (DEC-024) y overrides (DEC-025).
export function recalcularFila(fila, tipos, usos) {
  const tipo = tipos.find((t) => t.id === fila.tipo_id);
  const uso = usos.find((u) => u.id === fila.uso_id);
  const ov = fila.overrides || {};
  const manual = new Set(fila.campos_manuales || []);

  const consumo_base = ov.consumo_base ?? tipo?.consumo_base ?? 0;
  const sin_base = ov.sin_pct_base ?? tipo?.siniestralidad_base ?? 0;
  const valor_base = ov.valor_unitario_base ?? tipo?.valor_unitario ?? 0;
  const mult_c = ov.multiplicador_consumo ?? uso?.multiplicador_consumo ?? 1;
  const mult_s = ov.multiplicador_siniestros ?? uso?.multiplicador_siniestros ?? 1;

  return {
    ...fila,
    consumo: manual.has("consumo") ? fila.consumo : round1(consumo_base * mult_c),
    sin_pct: manual.has("sin_pct") ? fila.sin_pct : round1(sin_base * mult_s),
    valor_unitario: manual.has("valor_unitario") ? fila.valor_unitario : Math.round(valor_base),
  };
}

export default function ComposicionFlota({ filas, onChange }) {
  const [tipos, setTipos] = useState([]);
  const [usos, setUsos] = useState([]);
  const [expandidas, setExpandidas] = useState({});  // { [filaId]: true }

  useEffect(() => {
    (async () => {
      try {
        const [t, u] = await Promise.all([
          authAxios.get(`${API}/configuracion-constructor/tipos-vehiculo`),
          authAxios.get(`${API}/configuracion-constructor/usos-vehiculo`),
        ]);
        setTipos(t.data || []);
        setUsos(u.data || []);
      } catch { /* silencioso */ }
    })();
  }, []);

  const totales = useMemo(() => calcularTotales(filas), [filas]);

  function emit(nuevas) { onChange(nuevas, calcularTotales(nuevas)); }

  function setFila(idx, patch) {
    const nuevas = filas.map((f, i) => (i === idx ? { ...f, ...patch } : f));
    emit(nuevas);
  }

  // Cambia tipo o uso · aplica reglas DEC-025
  function cambiarTipoOUso(idx, campo, valor) {
    const f = filas[idx];
    const overrides = { ...(f.overrides || {}) };
    if (campo === "tipo_id") {
      // Al cambiar tipo se pierden overrides de base. Los de multiplicador se conservan.
      delete overrides.consumo_base;
      delete overrides.sin_pct_base;
      delete overrides.valor_unitario_base;
    } else if (campo === "uso_id") {
      // Al cambiar uso se pierden overrides de multiplicador. Los de base se conservan.
      delete overrides.multiplicador_consumo;
      delete overrides.multiplicador_siniestros;
    }
    const nueva = recalcularFila({ ...f, [campo]: valor, overrides }, tipos, usos);
    emit(filas.map((x, i) => (i === idx ? nueva : x)));
  }

  // Edición directa de Consumo / Sin% / Valor → marca como manual
  function editarCampoEfectivo(idx, campo, valor) {
    const f = filas[idx];
    const manuales = new Set(f.campos_manuales || []);
    manuales.add(campo);
    setFila(idx, { [campo]: valor, campos_manuales: Array.from(manuales) });
  }

  // Edición de override · limpia campo_manual correspondiente y recalcula
  function editarOverride(idx, clave, valor) {
    const f = filas[idx];
    const overrides = { ...(f.overrides || {}) };
    if (valor === "" || valor === null || isNaN(valor)) delete overrides[clave];
    else overrides[clave] = Number(valor);
    // Limpia manuales que volverán a ser auto
    const manuales = new Set(f.campos_manuales || []);
    if (clave === "consumo_base" || clave === "multiplicador_consumo") manuales.delete("consumo");
    if (clave === "sin_pct_base" || clave === "multiplicador_siniestros") manuales.delete("sin_pct");
    if (clave === "valor_unitario_base") manuales.delete("valor_unitario");
    const nueva = recalcularFila({ ...f, overrides, campos_manuales: Array.from(manuales) }, tipos, usos);
    emit(filas.map((x, i) => (i === idx ? nueva : x)));
  }

  // Reset total a Configuración
  function resetFila(idx) {
    const f = filas[idx];
    const nueva = recalcularFila({ ...f, overrides: {}, campos_manuales: [] }, tipos, usos);
    emit(filas.map((x, i) => (i === idx ? nueva : x)));
  }

  function eliminarFila(idx) { emit(filas.filter((_, i) => i !== idx)); }

  function agregarFila() {
    const tipo = tipos[0];
    const uso = usos[0];
    const nueva = recalcularFila({
      id: uid(),
      tipo_id: tipo?.id || "",
      uso_id: uso?.id || "",
      num_vehiculos: 0,
      km_anio: uso?.km_anio_tipico || 0,
      consumo: 0, sin_pct: 0, valor_unitario: 0,
      campos_manuales: [],
      overrides: {},
    }, tipos, usos);
    emit([...filas, nueva]);
  }

  return (
    <div className="bg-emerald-50/60 border border-emerald-200/60 rounded-2xl p-4 shadow-sm" data-testid="composicion-flota">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-[11px] uppercase tracking-widest text-emerald-700 font-bold">
          Composición flota · tipo × uso
        </h3>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-slate-500">
            <tr>
              <th className="w-6"></th>
              <th className="text-left p-1">Tipo</th>
              <th className="text-left p-1">Uso</th>
              <th className="text-right p-1">Veh.</th>
              <th className="text-right p-1">Km/año</th>
              <th className="text-right p-1 bg-amber-50">Consumo</th>
              <th className="text-right p-1 bg-amber-50">
                <span className="relative group inline-flex items-center justify-end">
                  Sin %
                  <span className="ml-1 inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-slate-300 text-slate-600 text-[9px] font-bold cursor-help" data-testid="composicion-sin-tooltip-trigger">?</span>
                  <span className="absolute bottom-5 right-0 hidden group-hover:block bg-slate-800 text-white text-[11px] normal-case px-2 py-1.5 rounded-lg whitespace-nowrap z-30 shadow-lg" data-testid="composicion-sin-tooltip">
                    Siniestralidad estimada como % del valor del vehículo al año
                  </span>
                </span>
              </th>
              <th className="text-right p-1 bg-amber-50">Valor activo</th>
              <th className="w-6"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-emerald-200/40">
            {filas.length === 0 && (
              <tr>
                <td colSpan={9} className="p-3 text-center text-xs text-slate-400 italic" data-testid="composicion-flota-vacia">
                  <button onClick={agregarFila} data-testid="composicion-flota-add-empty"
                    className="text-xs px-2.5 py-1 bg-white border border-emerald-300 rounded-full hover:bg-emerald-50 text-emerald-700 font-semibold inline-flex items-center gap-1">
                    <Plus className="w-3 h-3" /> Añadir primera fila
                  </button>
                </td>
              </tr>
            )}
            {filas.map((f, idx) => (
              <FilaComposicion
                key={f.id || idx}
                fila={f}
                idx={idx}
                tipos={tipos}
                usos={usos}
                expandida={!!expandidas[f.id]}
                onToggle={() => setExpandidas((e) => ({ ...e, [f.id]: !e[f.id] }))}
                onCambiarTipoUso={(campo, valor) => cambiarTipoOUso(idx, campo, valor)}
                onEditarCampo={(campo, valor) => editarCampoEfectivo(idx, campo, valor)}
                onEditarOverride={(clave, valor) => editarOverride(idx, clave, valor)}
                onReset={() => resetFila(idx)}
                onEliminar={() => eliminarFila(idx)}
                onSetFila={(patch) => setFila(idx, patch)}
                esUltima={idx === filas.length - 1}
                onAdd={agregarFila}
              />
            ))}
          </tbody>
          {filas.length > 0 && (
            <tfoot className="text-sm font-bold text-emerald-800 border-t-2 border-emerald-300">
              <tr data-testid="composicion-totales">
                <td></td>
                <td className="pt-2" colSpan={2}>Total flota</td>
                <td className="text-right pt-2">{totales.num_vehiculos_total.toLocaleString("es-ES")}</td>
                <td className="text-right pt-2">≈ {totales.km_anio_medio.toLocaleString("es-ES")}</td>
                <td colSpan={2}></td>
                <td className="text-right pt-2 bg-amber-50/40">{(totales.valor_activo_total / 1e6).toFixed(2)}M €</td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <p className="mt-2 text-[10px] text-slate-500">
        <span className="inline-block w-2 h-2 bg-amber-100 border border-amber-300 rounded-sm align-middle"></span> auto desde Configuración ·{" "}
        <span className="inline-block w-2 h-2 bg-rose-100 border border-rose-300 rounded-sm align-middle"></span> editado manualmente ·
        click en <ChevronRight className="inline w-3 h-3" /> para ajustar parámetros base y multiplicadores (DEC-025).
      </p>
    </div>
  );
}

// -----------------------------------------------------------------------------

function FilaComposicion({ fila, idx, tipos, usos, expandida, onToggle, onCambiarTipoUso, onEditarCampo, onEditarOverride, onReset, onEliminar, onSetFila, esUltima, onAdd }) {
  const tipo = tipos.find((t) => t.id === fila.tipo_id);
  const uso = usos.find((u) => u.id === fila.uso_id);
  const manuales = new Set(fila.campos_manuales || []);
  const ov = fila.overrides || {};
  const tieneOverrides = Object.keys(ov).length > 0;
  const tieneManuales = manuales.size > 0;

  const consumo_base_ef = ov.consumo_base ?? tipo?.consumo_base ?? 0;
  const sin_base_ef = ov.sin_pct_base ?? tipo?.siniestralidad_base ?? 0;
  const valor_base_ef = ov.valor_unitario_base ?? tipo?.valor_unitario ?? 0;
  const mult_c_ef = ov.multiplicador_consumo ?? uso?.multiplicador_consumo ?? 1;
  const mult_s_ef = ov.multiplicador_siniestros ?? uso?.multiplicador_siniestros ?? 1;

  const clsAmber = (campo) => (manuales.has(campo) ? "bg-white border-rose-300 text-slate-900 font-bold" : "bg-white border-amber-200 text-amber-900 font-semibold");
  const tdAmber = (campo) => (manuales.has(campo) ? "p-1 bg-rose-50" : "p-1 bg-amber-50/40");

  return (
    <>
      <tr data-testid={`composicion-fila-${idx}`} className={expandida ? "bg-amber-50/20" : ""}>
        <td className="p-1 text-center">
          <div className="inline-flex items-center gap-1">
            <button onClick={onToggle} data-testid={`composicion-expand-${idx}`}
              className={`hover:text-emerald-700 ${tieneOverrides ? "text-amber-600" : "text-slate-400"}`}>
              {expandida ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
            {esUltima && (
              <button onClick={onAdd} data-testid={`composicion-add-fila-${idx}`}
                title="Añadir fila"
                className="text-emerald-600 hover:text-emerald-800 hover:bg-emerald-100 rounded-full transition">
                <Plus className="w-4 h-4" />
              </button>
            )}
          </div>
        </td>
        <td className="p-1">
          <select value={fila.tipo_id || ""} onChange={(e) => onCambiarTipoUso("tipo_id", e.target.value)}
            data-testid={`composicion-tipo-${idx}`}
            className="text-sm bg-white border border-slate-200 rounded px-2 py-1 w-full">
            <option value="">—</option>
            {tipos.map((t) => <option key={t.id} value={t.id}>{t.nombre}</option>)}
          </select>
        </td>
        <td className="p-1">
          <select value={fila.uso_id || ""} onChange={(e) => onCambiarTipoUso("uso_id", e.target.value)}
            data-testid={`composicion-uso-${idx}`}
            className="text-sm bg-white border border-slate-200 rounded px-2 py-1 w-full">
            <option value="">—</option>
            {usos.map((u) => <option key={u.id} value={u.id}>{u.nombre}</option>)}
          </select>
        </td>
        <td className="p-1">
          <input type="number" min="0" value={fila.num_vehiculos ?? 0}
            onChange={(e) => onSetFila({ num_vehiculos: e.target.value === "" ? 0 : Number(e.target.value) })}
            data-testid={`composicion-veh-${idx}`}
            className="w-16 text-right text-sm bg-white border border-slate-200 rounded px-2 py-1 font-bold" />
        </td>
        <td className="p-1">
          <input type="number" min="0" value={fila.km_anio ?? 0}
            onChange={(e) => onSetFila({ km_anio: e.target.value === "" ? 0 : Number(e.target.value) })}
            data-testid={`composicion-km-${idx}`}
            className="w-20 text-right text-sm bg-white border border-slate-200 rounded px-2 py-1" />
        </td>
        <td className={tdAmber("consumo")}>
          <input type="number" step="0.1" value={fila.consumo ?? 0}
            onChange={(e) => onEditarCampo("consumo", e.target.value === "" ? 0 : Number(e.target.value))}
            data-testid={`composicion-consumo-${idx}`}
            className={`w-16 text-right text-sm border rounded px-2 py-1 ${clsAmber("consumo")}`} />
        </td>
        <td className={tdAmber("sin_pct")}>
          <input type="number" step="0.1" value={fila.sin_pct ?? 0}
            onChange={(e) => onEditarCampo("sin_pct", e.target.value === "" ? 0 : Number(e.target.value))}
            data-testid={`composicion-sin-${idx}`}
            className={`w-14 text-right text-sm border rounded px-2 py-1 ${clsAmber("sin_pct")}`} />
        </td>
        <td className={tdAmber("valor_unitario")}>
          <input type="number" value={fila.valor_unitario ?? 0}
            onChange={(e) => onEditarCampo("valor_unitario", e.target.value === "" ? 0 : Number(e.target.value))}
            data-testid={`composicion-valor-${idx}`}
            className={`w-24 text-right text-sm border rounded px-2 py-1 ${clsAmber("valor_unitario")}`} />
        </td>
        <td className="p-1 text-center">
          <button onClick={onEliminar} data-testid={`composicion-del-${idx}`} className="text-slate-400 hover:text-rose-500">
            <X className="w-3.5 h-3.5" />
          </button>
        </td>
      </tr>

      {/* Panel expandido · overrides */}
      {expandida && (
        <tr className="bg-gradient-to-r from-amber-50 to-white border-l-4 border-amber-400" data-testid={`composicion-override-${idx}`}>
          <td></td>
          <td colSpan={8} className="px-3 py-3">
            <div className="flex items-start gap-4 flex-wrap">
              <div className="flex-shrink-0">
                <div className="text-[9px] uppercase tracking-widest text-amber-700 font-bold mb-1">
                  Override base · {tipo?.nombre || "—"}
                </div>
                <div className="flex items-center gap-1 text-xs flex-wrap">
                  <span className="text-slate-500">Cons.</span>
                  <input type="number" step="0.1" value={consumo_base_ef}
                    onChange={(e) => onEditarOverride("consumo_base", e.target.value)}
                    data-testid={`override-consumo-base-${idx}`}
                    className={`w-14 text-right px-1.5 py-1 bg-white border rounded ${ov.consumo_base !== undefined ? "border-rose-300 text-slate-900 font-bold" : "border-slate-200"}`} />
                  <span className="text-slate-400">L/100</span>
                  <span className="text-slate-300 mx-1">·</span>
                  <span className="text-slate-500">Sin%</span>
                  <input type="number" step="0.1" value={sin_base_ef}
                    onChange={(e) => onEditarOverride("sin_pct_base", e.target.value)}
                    data-testid={`override-sin-base-${idx}`}
                    className={`w-12 text-right px-1.5 py-1 bg-white border rounded ${ov.sin_pct_base !== undefined ? "border-rose-300 text-slate-900 font-bold" : "border-slate-200"}`} />
                  <span className="text-slate-300 mx-1">·</span>
                  <span className="text-slate-500">Valor</span>
                  <input type="number" value={valor_base_ef}
                    onChange={(e) => onEditarOverride("valor_unitario_base", e.target.value)}
                    data-testid={`override-valor-base-${idx}`}
                    className={`w-20 text-right px-1.5 py-1 bg-white border rounded ${ov.valor_unitario_base !== undefined ? "border-rose-300 text-slate-900 font-bold" : "border-slate-200"}`} />
                  <span className="text-slate-400">€</span>
                </div>
                {tipo && (
                  <div className="text-[9px] text-slate-400 mt-0.5">
                    Config: {tipo.consumo_base} L/100 · {tipo.siniestralidad_base}% · {Number(tipo.valor_unitario).toLocaleString("es-ES")}€
                    {tipo.fuente && <span className="ml-1 italic">· {tipo.fuente}</span>}
                  </div>
                )}
              </div>

              <div className="flex-shrink-0 border-l border-amber-200 pl-4">
                <div className="text-[9px] uppercase tracking-widest text-amber-700 font-bold mb-1">
                  Override multiplicadores · {uso?.nombre || "—"}
                </div>
                <div className="flex items-center gap-1 text-xs flex-wrap">
                  <span className="text-slate-500">×Cons.</span>
                  <input type="number" step="0.05" value={mult_c_ef}
                    onChange={(e) => onEditarOverride("multiplicador_consumo", e.target.value)}
                    data-testid={`override-mult-cons-${idx}`}
                    className={`w-14 text-right px-1.5 py-1 bg-white border rounded ${ov.multiplicador_consumo !== undefined ? "border-rose-300 text-slate-900 font-bold" : "border-slate-200"}`} />
                  <span className="text-slate-300 mx-1">·</span>
                  <span className="text-slate-500">×Sin</span>
                  <input type="number" step="0.05" value={mult_s_ef}
                    onChange={(e) => onEditarOverride("multiplicador_siniestros", e.target.value)}
                    data-testid={`override-mult-sin-${idx}`}
                    className={`w-14 text-right px-1.5 py-1 bg-white border rounded ${ov.multiplicador_siniestros !== undefined ? "border-rose-300 text-slate-900 font-bold" : "border-slate-200"}`} />
                </div>
                {uso && (
                  <div className="text-[9px] text-slate-400 mt-0.5">
                    Config: ×{uso.multiplicador_consumo} cons · ×{uso.multiplicador_siniestros} sin
                    {uso.fuente && <span className="ml-1 italic">· {uso.fuente}</span>}
                  </div>
                )}
              </div>

              <div className="flex-shrink-0 ml-auto flex items-center gap-2">
                {(tieneOverrides || tieneManuales) && (
                  <button onClick={onReset} data-testid={`composicion-reset-${idx}`}
                    className="px-2.5 py-1 text-[10px] text-slate-600 border border-slate-300 rounded-full hover:bg-slate-50 font-semibold inline-flex items-center gap-1">
                    <RotateCcw className="w-3 h-3" /> Reset a Configuración
                  </button>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}

      {/* Rastro gris · sólo si NO está expandida */}
      {!expandida && tipo && uso && (
        <tr className="bg-slate-50/60">
          <td></td>
          <td colSpan={8} className="px-2 pb-2 pt-0.5">
            <div className="text-[10px] text-slate-500 leading-relaxed font-mono">
              <span className={`inline-block w-2 h-2 rounded-full mr-1.5 align-middle ${tieneManuales || tieneOverrides ? "bg-rose-400" : "bg-emerald-400"}`}></span>
              {tipo.nombre} ({consumo_base_ef} · {sin_base_ef}% · {Number(valor_base_ef).toLocaleString("es-ES")}€){ov.consumo_base !== undefined || ov.sin_pct_base !== undefined || ov.valor_unitario_base !== undefined ? " [override]" : ""}
              {" × "}
              {uso.nombre} (×{mult_c_ef} · ×{mult_s_ef}){ov.multiplicador_consumo !== undefined || ov.multiplicador_siniestros !== undefined ? " [override]" : ""}
              {" → "}
              <span className={tieneManuales ? "text-rose-700 font-semibold" : "text-emerald-700 font-semibold"}>
                {fila.consumo} · {fila.sin_pct}% · {Number(fila.valor_unitario).toLocaleString("es-ES")}€
              </span>
              {tieneManuales && (
                <span className="ml-2 text-rose-600">· {Array.from(manuales).join(", ")} editado a mano</span>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
